<template>
  <div class="min-h-screen flex flex-col bg-gray-50">
    <!-- Header -->
    <header class="w-full bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 border-b border-slate-700 shadow-lg">
      <div class="w-full px-6 sm:px-10 lg:px-16">
        <div class="flex items-center justify-between h-16">
          <div class="flex items-center flex-shrink-0">
            <span class="text-white font-bold text-xl tracking-wide">EMDEU</span>
          </div>
          <div>
            <LanguageSwitcher />
          </div>
        </div>
      </div>
    </header>

    <!-- Main Content -->
    <div class="flex-1 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div class="max-w-md w-full space-y-8">
        <div class="text-center">
          <h2 class="text-center text-2xl font-semibold text-gray-900">
            {{ $t('auth.loginTitle', 'Вход в систему') }}
          </h2>
          <p class="mt-2 text-center text-sm text-gray-600">
            {{ $t('auth.loginSubtitle', 'Войдите в свою учетную запись') }}
          </p>
        </div>
      
      <div class="mt-8 bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
        <form class="space-y-6" @submit.prevent="handleLogin">
          <div v-if="error" class="rounded-md p-4 mb-4" :class="getErrorBackgroundClass()">
            <div class="flex items-start">
              <div class="flex-shrink-0">
                <!-- Warning icon for attempts remaining -->
                <svg v-if="remainingAttempts !== null && remainingAttempts > 0" class="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd" />
                </svg>
                <!-- Block/error icon -->
                <svg v-else class="h-5 w-5" :class="isBlocked ? 'text-red-400' : 'text-red-400'" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clip-rule="evenodd" />
                </svg>
              </div>
              <div class="ml-3">
                <h3 class="text-sm font-medium" :class="getErrorTextClass()">{{ error }}</h3>
                <!-- Progress bar showing remaining attempts -->
                <div v-if="remainingAttempts !== null && remainingAttempts > 0 && remainingAttempts <= 3" class="mt-2">
                  <div class="w-full bg-gray-200 rounded-full h-1.5">
                    <div class="h-1.5 rounded-full transition-all duration-300" 
                         :class="getProgressBarClass()" 
                         :style="{ width: (remainingAttempts / 5 * 100) + '%' }">
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div>
            <label for="email" class="block text-sm font-medium text-gray-700">
              {{ $t('auth.email', 'Email') }}
            </label>
            <div class="mt-1">
              <input 
                id="email" 
                name="email" 
                type="email" 
                autocomplete="email" 
                required 
                v-model="email"
                :class="{'border-red-500': emailError}"
                class="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" 
                :placeholder="$t('auth.emailPlaceholder', 'Введите ваш email')"
              />
            </div>
            <p v-if="emailError" class="mt-2 text-sm text-red-600">{{ emailError }}</p>
          </div>

          <div>
            <label for="password" class="block text-sm font-medium text-gray-700">
              {{ $t('auth.password', 'Пароль') }}
            </label>
            <div class="mt-1">
              <input 
                id="password" 
                name="password" 
                type="password" 
                autocomplete="current-password" 
                required 
                v-model="password"
                :class="{'border-red-500': passwordError}"
                class="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                :placeholder="$t('auth.passwordPlaceholder', 'Введите ваш пароль')"
              />
            </div>
            <p v-if="passwordError" class="mt-2 text-sm text-red-600">{{ passwordError }}</p>
          </div>

          <div class="flex items-center justify-between">
            <div class="flex items-center">
              <input 
                id="remember-me" 
                name="remember-me" 
                type="checkbox" 
                v-model="rememberMe"
                class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" 
              />
              <label for="remember-me" class="ml-2 block text-sm text-gray-900">
                {{ $t('auth.rememberMe', 'Запомнить меня') }}
              </label>
            </div>

            <div class="text-sm">
              <router-link to="/forgot-password" class="font-medium text-blue-600 hover:text-blue-500">
                {{ $t('auth.forgotPassword', 'Забыли пароль?') }}
              </router-link>
            </div>
          </div>

          <div>
            <button 
              type="submit" 
              :disabled="loading"
              class="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-300 disabled:cursor-not-allowed transition-colors"
            >
              <span v-if="loading" class="mr-2">
                <HeartbeatLoader :size="16" />
              </span>
              {{ $t('auth.loginButton', 'Войти') }}
            </button>
          </div>
        </form>
      </div>
    </div>
    </div>

    <!-- Footer -->
    <footer class="w-full">
      <!-- Upper part - white background -->
      <div class="bg-white border-t border-gray-200">
        <div class="w-full px-6 sm:px-10 lg:px-16 py-6">
          <div class="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <!-- Logo - left aligned -->
            <div class="flex items-center flex-shrink-0">
              <span class="text-blue-600 font-bold text-xl tracking-wide">EMDEU</span>
            </div>
            
            <!-- Links - right aligned -->
            <nav class="flex flex-wrap justify-end gap-x-6 gap-y-2 text-sm">
              <router-link to="/license-agreement" class="text-blue-600 hover:text-blue-500 transition-colors">
                {{ $t('documents.licenseAgreement', 'Лицензионный договор') }}
              </router-link>
              <router-link to="/privacy-policy" class="text-blue-600 hover:text-blue-500 transition-colors">
                {{ $t('documents.privacyPolicy', 'Политика конфиденциальности') }}
              </router-link>
              <router-link to="/user-agreement" class="text-blue-600 hover:text-blue-500 transition-colors">
                {{ $t('documents.userAgreement', 'Пользовательское соглашение') }}
              </router-link>
              <router-link to="/service-terms" class="text-blue-600 hover:text-blue-500 transition-colors">
                {{ $t('documents.serviceTerms', 'Порядок и сроки оказания услуг') }}
              </router-link>
            </nav>
          </div>
        </div>
      </div>
      
      <!-- Lower part - dark background with copyright -->
      <div class="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900">
        <div class="w-full px-6 sm:px-10 lg:px-16 py-3">
          <p class="text-slate-400 text-sm">© 2026 {{ $t('footer.allRightsReserved', 'Все права защищены') }}</p>
        </div>
      </div>
    </footer>
    
    <!-- EDS Authentication Modal -->
    <EDSAuthModal 
      :show="showEDSModal"
      @success="onEDSSuccess"
      @cancel="onEDSCancel"
      @skip="onEDSSkip"
    />
  </div>
</template>

<script>
import api from '../services/api'
import LanguageSwitcher from '../components/LanguageSwitcher.vue'
import HeartbeatLoader from '../components/common/HeartbeatLoader.vue'
import EDSAuthModal from '../components/EDSAuthModal.vue'
import { useI18n } from 'vue-i18n'

export default {
  name: 'LoginPage',
  components: {
    LanguageSwitcher,
    HeartbeatLoader,
    EDSAuthModal
  },
  setup() {
    const { t } = useI18n()
    return { t }
  },
  data() {
    return {
      email: '',
      password: '',
      rememberMe: false,
      loading: false,
      error: null,
      emailError: null,
      passwordError: null,
      remainingAttempts: null,
      isBlocked: false,
      blockedUntil: null,
      remainingMinutes: null,
      // EDS 2FA
      showEDSModal: false,
      pendingLoginResponse: null
    }
  },
  methods: {
    getErrorBackgroundClass() {
      if (this.isBlocked) {
        return 'bg-red-50'
      } else if (this.remainingAttempts !== null && this.remainingAttempts <= 3) {
        if (this.remainingAttempts === 1) {
          return 'bg-orange-50'
        }
        return 'bg-yellow-50'
      }
      return 'bg-red-50'
    },
    getErrorTextClass() {
      if (this.isBlocked) {
        return 'text-red-800'
      } else if (this.remainingAttempts !== null && this.remainingAttempts <= 3) {
        if (this.remainingAttempts === 1) {
          return 'text-orange-800'
        }
        return 'text-yellow-800'
      }
      return 'text-red-800'
    },
    getProgressBarClass() {
      if (this.remainingAttempts === 1) {
        return 'bg-orange-500'
      } else if (this.remainingAttempts === 2) {
        return 'bg-yellow-500'
      }
      return 'bg-yellow-400'
    },
    validateEmail() {
      this.emailError = null
      
      if (!this.email) {
        this.emailError = this.t('auth.emailRequired', 'Email обязателен для заполнения')
        return false
      }
      
      // Basic email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(this.email)) {
        this.emailError = this.t('auth.emailInvalid', 'Введите корректный email')
        return false
      }
      
      return true
    },
    validatePassword() {
      this.passwordError = null
      
      if (!this.password) {
        this.passwordError = this.t('auth.passwordRequired', 'Пароль обязателен для заполнения')
        return false
      }
      
      if (this.password.length < 6) {
        this.passwordError = this.t('auth.passwordMinLength', 'Пароль должен содержать минимум 6 символов')
        return false
      }
      
      return true
    },
    async handleLogin() {
      // Reset errors
      this.error = null
      this.emailError = null
      this.passwordError = null
      
      // Validate inputs
      const isEmailValid = this.validateEmail()
      const isPasswordValid = this.validatePassword()
      
      if (!isEmailValid || !isPasswordValid) {
        return
      }
      
      this.loading = true
      
      try {
        const response = await api.login(this.email, this.password, this.rememberMe)
        
        // Проверяем, требуется ли смена пароля
        if (response.data.must_change_password || response.data.password_expired) {
          const queryParams = {}
          if (response.data.must_change_password) {
            queryParams.mandatory = 'true'
          }
          if (response.data.password_expired) {
            queryParams.expired = 'true'
          }
          
          // Перенаправляем на страницу смены пароля
          this.$router.push({ 
            path: '/change-password', 
            query: queryParams 
          })
          return
        }
        
        // Проверяем, требуется ли 2FA по ЭЦП
        if (response.data.requires_eds_verification) {
          // Сохраняем ответ для обработки после верификации ЭЦП
          this.pendingLoginResponse = response
          this.showEDSModal = true
          this.loading = false
          return
        }
        
        // Продолжаем стандартный редирект
        await this.completeLogin(response)
        
      } catch (error) {
        // Reset previous state
        this.remainingAttempts = null
        this.isBlocked = false
        this.blockedUntil = null
        this.remainingMinutes = null
        
        if (error.response) {
          const data = error.response.data
          
          if (error.response.status === 429) {
            // Account is blocked
            this.isBlocked = true
            this.blockedUntil = data.blocked_until
            this.remainingMinutes = data.remaining_minutes || 5
            this.error = this.t('auth.accountBlocked', `Вход заблокирован. Попробуйте через ${this.remainingMinutes} минут.`)
          } else if (error.response.status === 401) {
            const remaining = data.remaining_attempts
            this.remainingAttempts = remaining
            
            if (remaining !== undefined && remaining <= 3) {
              // Show warning after 2 failed attempts (3 or fewer remaining)
              if (remaining === 0) {
                this.error = this.t('auth.noAttemptsLeft', 'Вход заблокирован на 5 минут из-за превышения количества попыток.')
                this.isBlocked = true
              } else if (remaining === 1) {
                this.error = this.t('auth.lastAttempt', 'Неверный пароль. Осталась последняя попытка!')
              } else {
                this.error = this.t('auth.attemptsRemaining', `Неверный пароль. Осталось попыток: ${remaining}`)
              }
            } else {
              this.error = this.t('auth.loginError', 'Неверный email или пароль')
            }
          } else {
            this.error = this.t('messages.serverError', 'Ошибка сервера. Попробуйте позже.')
          }
        } else {
          this.error = this.t('messages.serverError', 'Ошибка сервера. Попробуйте позже.')
        }
      } finally {
        this.loading = false
      }
    },
    
    // Завершение логина после успешной аутентификации (или после EDS верификации)
    async completeLogin(response) {
      // Check if there's a redirect parameter
      const redirectUrl = this.$route.query.redirect
      
      if (redirectUrl) {
        // If there's a redirect URL, use it
        this.$router.push(decodeURIComponent(redirectUrl)).then(() => {
          window.location.reload()
        })
      } else {
        // Check user type and types to determine redirect
        const userTypes = response.data.user_types || [] // Все роли
        const userInfo = response.data.user
        
        // Определяем приоритет перенаправления на основе ролей
        if (userInfo?.is_superuser) {
          // Администратор
          this.$router.push('/admin/dashboard').then(() => {
            window.location.reload()
          })
        } else if (userInfo?.is_staff || userTypes.includes('staff')) {
          // Персонал - направляем на панель персонала
          const staffRedirect = userInfo?.staff_role === 'clinic_manager'
            ? '/clinic-appointments'
            : '/staff'

          this.$router.push(staffRedirect).then(() => {
            window.location.reload()
          })
        } else if (userTypes.includes('doctor')) {
          // Врач - проверяем расписание
          try {
            const schedulesResponse = await api.hasSchedules()
            const hasSchedules = schedulesResponse.data && schedulesResponse.data.has_schedules
            
            if (hasSchedules) {
              this.$router.push('/').then(() => {
                window.location.reload()
              })
            } else {
              this.$router.push('/doctor-schedule').then(() => {
                window.location.reload()
              })
            }
          } catch (scheduleError) {
            this.$router.push('/').then(() => {
              window.location.reload()
            })
          }
        } else if (userTypes.includes('nurse')) {
          // Медсестра
          this.$router.push('/nurse-calls-history').then(() => {
            window.location.reload()
          })
        } else if (userTypes.includes('patient')) {
          // Пациент - направляем в мобильный интерфейс
          this.$router.push('/patient').then(() => {
            window.location.reload()
          })
        } else {
          this.$router.push('/').then(() => {
            window.location.reload()
          })
        }
      }
    },
    
    // EDS Modal handlers
    onEDSSuccess() {
      this.showEDSModal = false
      // После успешной верификации ЭЦП, продолжаем логин
      if (this.pendingLoginResponse) {
        this.completeLogin(this.pendingLoginResponse)
      }
    },
    
    onEDSCancel() {
      this.showEDSModal = false
      this.pendingLoginResponse = null
      // Выходим из системы т.к. 2FA не пройден
      api.logout()
      this.error = this.t('eds.cancelled', 'Аутентификация по ЭЦП отменена')
    },
    
    onEDSSkip() {
      this.showEDSModal = false
      // TODO: В будущем можно разрешить пропуск, сейчас требуем обязательно
      this.error = this.t('eds.required', 'Аутентификация по ЭЦП обязательна для медсестёр')
      api.logout()
      this.pendingLoginResponse = null
    }
  }
}
</script> 
